import styled from '@emotion/styled';
import {BackupShelfItem} from 'electron-communication-lib/BrowserForApp';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Divider,
  EHeaderButton,
  EList,
  EListActiveEntry,
  EListItemEntry,
  EUndecoratedButton,
} from 'webapp-lib/Components';
import {
  mixinFlexboxRow,
  mixinFloatingSideBar,
  mixinSVGIcon,
} from 'webapp-lib/styling';

import {ExclamationMark, Info} from '_/assets/images';

import {showDeleteConfirmationDialog} from './BackupShelf.utils';
import {BackupShelfListComponent} from './BackupShelfListItem';

export const BackupShelf = ({
  isOpen,
  filesList,
  disabled,
  setIsOpen,
}: {
  isOpen: boolean;
  filesList: BackupShelfItem[];
  disabled?: boolean;
  setIsOpen: (open: boolean) => void;
}): React.ReactElement | null => {
  const {t} = useTranslation('documents');
  const [activeItem, setActiveItem] = useState<EListActiveEntry>();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const handleSelectAll = useCallback(() => {
    setSelectedItems(filesList.map((file) => file.path));
  }, [filesList]);

  const handleDeleteAll = useCallback(
    () => showDeleteConfirmationDialog(filesList),
    [filesList]
  );

  const filesInShelf = useMemo(
    () =>
      filesList.map(
        (fileInfo: BackupShelfItem): EListItemEntry<BackupShelfItem> => ({
          id: fileInfo.path,
          raw: fileInfo,
          Component: BackupShelfListComponent,
          options: {
            disableDragStyle: true,
            isSelectable: true,
            isActivatable: true,
            onDragStart: (evt) => {
              evt.preventDefault();

              const files = [
                ...new Set([
                  ...selectedItems
                    .map(
                      (selectedPath) =>
                        filesList.find((file) => file.path === selectedPath)
                          ?.path
                    )
                    .filter((filter) => filter),
                  fileInfo.path,
                ]),
              ];
              // rule disabled because the api elements on window gets defined by electron
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.api?.startDrag(files);
            },
          },
        })
      ),
    [filesList, selectedItems]
  );

  if (!filesList.length) {
    return null;
  }

  return (
    <BackupShelfWrapper>
      <EHeaderButton
        contentFlag={{
          content: filesList.length.toString(),
          mode: 'warning',
        }}
        disabled={disabled}
        isActive={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <ExclamationMark />
      </EHeaderButton>
      <Divider aria-hidden='true' className='Divider' orientation='vertical' />
      {isOpen && (
        <SideBar>
          <BackupShelfInfo>
            <Info />
            <p>{t('backupShelf.info')}</p>
          </BackupShelfInfo>
          <ListHeader>
            <EUndecoratedButton onClick={handleSelectAll}>
              {t('backupShelf.selectAll')}
            </EUndecoratedButton>
            <EUndecoratedButton onClick={handleDeleteAll}>
              {t('backupShelf.deleteAll')}
            </EUndecoratedButton>
          </ListHeader>

          <EList
            activeItem={activeItem}
            data={filesInShelf}
            placeholder={t('backupShelf.placeholder')}
            selectedItems={selectedItems}
            onActiveItemChange={setActiveItem}
            onSelectedItemsChange={setSelectedItems}
          />
        </SideBar>
      )}
    </BackupShelfWrapper>
  );
};

const BackupShelfWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--gutter-medium);
  height: 100%;

  .EHeaderButton svg {
    color: var(--color-notification-important);
  }

  .Divider {
    height: 60%;
    border-color: var(--color-core-white);
  }
`;

const BackupShelfInfo = styled.div`
  display: flex;
  gap: var(--gutter-tiny);
  padding: var(--gutter-small);
  color: var(--color-core-primary);
  background-color: var(--color-core-cockpit-boxes);
  border-bottom: 1px solid var(--color-core-grey);

  > svg {
    ${mixinSVGIcon}
  }

  > p {
    font-size: 14px;
    line-height: 1.2;
  }
`;

const SideBar = styled.div`
  ${mixinFloatingSideBar};
`;

const ListHeader = styled.div`
  ${mixinFlexboxRow};
  padding: var(--gutter-small);
  border-bottom: var(--regular-border-width) solid var(--color-core-primary);
`;
